import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { goBack } from 'react-router-redux';

import {
  BookingRelationList,
  DriverAvatar,
  DriverUpdateForm,
  HeadingSection,
  NoteList,
  Permission,
  RouteWrapper,
  PaymentTable,
  IconButton,
  Section,
  Tooltip,
} from '../../components';

import {
  getDriverBookings,
  getDriverPayments,
  getDriver,
  getRevision,
} from '../../redux/actions/drivers';

interface Props {
  drivers: Array<any>;
  vehicleTypes: Array<any>;
  match: any;
  dispatch: Function;
}

interface State {
  id: number;
  revisionId?: number;
  driver: any;
  ready: boolean;
  section: 0 | 1 | 2;
  bookingsReady: boolean;
  bookings: Array<any>;
  error?: string;
}

class Route extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      id: Number(this.props.match.params.id),
      revisionId: Number(this.props.match.params.revisionId),
      driver: false,
      ready: false,
      section: 0,
      bookingsReady: true,
      bookings: [],
    };
    this.getDriverBookings = this.getDriverBookings.bind(this);
  }

  /**
   * Load driver from the API
   */
  async componentDidMount() {
    let response;
    if (this.state.revisionId) {
      response = await this.props.dispatch(
        getRevision(this.state.id, this.state.revisionId),
      );
    } else {
      response = await this.props.dispatch(getDriver(this.state.id));
    }
    if (response.error) {
      this.setState({ error: response.error });
      return;
    }
    this.setState({
      driver: response.data,
      ready: true,
    });
  }

  /**
   * Get the bookings assigned to the driver
   */
  async getDriverBookings(page: number = 0) {
    this.setState({ bookingsReady: false });
    const bookings = await this.props.dispatch(
      getDriverBookings(this.state.id, page),
    );
    this.setState({ bookings: bookings.data, bookingsReady: true });
  }

  render() {
    return (
      <RouteWrapper
        requireAuth
        requirePermission="MANAGE_DRIVERS"
        title="Driver"
        breadcrumbs={[
          { link: '/drivers', text: 'Drivers' },
          {
            link: `/drivers/${this.state.id}`,
            text: this.state.driver.name || 'Driver',
          },
        ]}
        ready={this.state.ready}
        error={this.state.error}
      >
        <HeadingSection
          title={this.state.driver.name}
          subtitle={
            this.state.driver.company_id ? (
              <Link to={`/companies/${this.state.driver.company_id}`}>
                {this.state.driver.company.name}
              </Link>
            ) : (
              undefined
            )
          }
          image={<DriverAvatar driver={this.state.driver} />}
          controls={
            !!this.state.revisionId
              ? []
              : [
                  <Permission key="delete" require="MANAGE_DELETIONS">
                    {this.state.bookings.length > 0 ? (
                      <Tooltip text="Driver is attached to a booking">
                        <button className="button is-static">
                          Deletion disabled
                        </button>
                      </Tooltip>
                    ) : (
                      <IconButton
                        link={`/drivers/${this.state.id}/delete`}
                        className="is-danger is-outlined"
                        icon="trash"
                      >
                        Delete
                      </IconButton>
                    )}
                  </Permission>,
                  <IconButton
                    key="back-button"
                    icon="undo"
                    className="is-default"
                    handleClick={() => {
                      this.props.dispatch(goBack());
                    }}
                  >
                    Return
                  </IconButton>,
                ]
          }
        />
        <Section>
          <div className="tabs is-boxed">
            <ul>
              {['Details', 'Bookings', 'Payments'].map((name, index) => (
                <li
                  key={name}
                  className={this.state.section === index ? 'is-active' : ''}
                >
                  <a
                    onClick={() => {
                      this.setState({ section: index as 0 | 1 });
                    }}
                  >
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="columns">
            <div className="column is-12-desktop is-7-widescreen">
              {this.state.section === 0 && (
                <DriverUpdateForm
                  driver={this.state.driver}
                  isDisabled={!!this.state.revisionId}
                  handleUpdate={() => {
                    window.history.back();
                  }}
                />
              )}
              {this.state.section === 1 && (
                <BookingRelationList
                  vehicleTypes={this.props.vehicleTypes}
                  bookings={this.state.bookings}
                  handleUpdate={(page) => this.getDriverBookings(page)}
                  loading={!this.state.bookingsReady}
                />
              )}
              {this.state.section === 2 && (
                <PaymentTable
                  id={this.state.id}
                  action={getDriverPayments}
                  vehicleTypes={this.props.vehicleTypes}
                  hideNotes
                />
              )}
            </div>
            <div className="column is-12-desktop is-5-widescreen">
              <NoteList resource="drivers" id={this.state.id} />
            </div>
          </div>
        </Section>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ drivers, vehicleTypes }) => ({
  drivers: drivers.drivers,
  vehicleTypes: vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(Route);
